import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { API, authHeaders, useAdminAuthError } from "@/lib/adminApi";
import { ADMIN_RECORDABLE_PROVIDERS, PROVIDER_LABELS } from "@/lib/adminUtils";
import PageHeader from "@/components/admin/PageHeader";
import KpiCard from "@/components/admin/KpiCard";
import Spinner from "@/components/admin/Spinner";
import { PanelField, labelCls } from "@/components/admin/NewMemberPanel";

const eur = (v) => (v == null ? "—" : `${Number(v).toLocaleString("fr-FR")} €`);
const pct = (v) => (v == null ? null : `${v >= 0 ? "+" : ""}${(v * 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`);

const MONTH_LABELS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

const todayIso = () => new Date().toISOString().slice(0, 10);
const EMPTY_DONATION_FORM = { donor_name: "", amount: "", method: "especes", received_at: todayIso(), reference: "", note: "" };

/* ── Finance — bilan annuel du fonds : recettes (cotisations + dons),
 *    dépenses (sinistres payés), résultat, solde cumulé. ── */
export const FinancePage = () => {
  const { year } = useOutletContext();
  const handleAuthError = useAdminAuthError();

  const [bilan, setBilan] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  const [showDonationForm, setShowDonationForm] = useState(false);
  const [donationForm, setDonationForm] = useState(EMPTY_DONATION_FORM);
  const [recordingDonation, setRecordingDonation] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bilanRes, donationsRes] = await Promise.all([
        axios.get(`${API}/finance/bilan?year=${year}`, { headers: authHeaders() }),
        axios.get(`${API}/donations/admin-list`, { headers: authHeaders() }),
      ]);
      setBilan(bilanRes.data);
      setDonations(donationsRes.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erreur lors du chargement du bilan.");
    } finally {
      setLoading(false);
    }
  }, [year, handleAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setDonationField = (key) => (e) => {
    const value = e.target.value;
    setDonationForm((f) => ({ ...f, [key]: value }));
  };

  const handleRecordDonation = async (e) => {
    e.preventDefault();
    const amount = parseFloat(donationForm.amount);
    if (!amount || amount <= 0) { toast.error("Le montant doit être positif."); return; }
    setRecordingDonation(true);
    try {
      await axios.post(
        `${API}/donations/admin-record`,
        {
          donor_name: donationForm.donor_name || undefined,
          amount,
          method: donationForm.method,
          received_at: donationForm.received_at || undefined,
          reference: donationForm.reference || undefined,
          note: donationForm.note || undefined,
        },
        { headers: authHeaders() }
      );
      toast.success("Don enregistré.");
      setShowDonationForm(false);
      setDonationForm(EMPTY_DONATION_FORM);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement du don.");
    } finally {
      setRecordingDonation(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API}/finance/bilan/export/pdf?year=${year}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bilan_${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors de la génération du PDF.");
    } finally {
      setExportingPdf(false);
    }
  };

  const revenue = bilan?.revenue;
  const expenses = bilan?.expenses;
  const change = bilan?.change_vs_previous;
  const contribPct = revenue?.total ? Math.round((revenue.contributions / revenue.total) * 100) : null;
  const netAccent = bilan?.net_result == null ? undefined : bilan.net_result >= 0 ? "#059669" : "#DC2626";

  const revenuePctLabel = pct(change?.revenue_total_pct);
  const expensesPctLabel = pct(change?.expenses_total_pct);

  const monthlyData = (bilan?.monthly || []).map((m) => ({
    month: MONTH_LABELS[m.month - 1],
    Recettes: m.revenue,
    Dépenses: m.expenses,
  }));

  return (
    <div className="space-y-10">
      {/* ── Bilan de l'exercice ── */}
      <section className="fade-up fade-up-1">
        <PageHeader
          title="Finance"
          subtitle={`Bilan — exercice ${year}`}
          actions={
            <button
              onClick={handleExportPdf}
              disabled={exportingPdf}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-50"
            >
              {exportingPdf ? (
                <svg className="w-4 h-4 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
              )}
              <span>{exportingPdf ? "Export…" : "Exporter PDF"}</span>
            </button>
          }
        />
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-px"
          style={{ backgroundColor: "#1F2937", borderRadius: "16px", overflow: "hidden" }}
        >
          <KpiCard
            label="Recettes totales"
            value={eur(revenue?.total)}
            sub={revenuePctLabel ? `${revenuePctLabel} vs ${year - 1}` : `vs ${year - 1} : —`}
            accent="#60A5FA"
            loading={loading}
          />
          <KpiCard
            label="Dépenses totales"
            value={eur(expenses?.total)}
            sub={expensesPctLabel ? `${expensesPctLabel} vs ${year - 1}` : `vs ${year - 1} : —`}
            accent="#D97706"
            loading={loading}
          />
          <KpiCard
            label="Résultat de l'exercice"
            value={eur(bilan?.net_result)}
            sub="recettes − dépenses"
            accent={netAccent}
            loading={loading}
          />
          <KpiCard
            label="Solde fin d'exercice"
            value={eur(bilan?.fund_balance_end_of_year)}
            sub={`cumulé au 31/12/${year}`}
            accent="#A78BFA"
            loading={loading}
          />
        </div>

        {contribPct != null && (
          <div className="mt-3 px-1">
            <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden flex">
              <div className="h-full" style={{ width: `${contribPct}%`, backgroundColor: "#60A5FA" }} />
              <div className="h-full" style={{ width: `${100 - contribPct}%`, backgroundColor: "#A78BFA" }} />
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-1.5">
              <span>Cotisations : {eur(revenue.contributions)} ({contribPct}%)</span>
              <span>Dons : {eur(revenue.donations)} ({100 - contribPct}%)</span>
            </div>
          </div>
        )}
      </section>

      {/* ── Évolution mensuelle ── */}
      <section className="fade-up fade-up-2">
        <PageHeader title="Évolution mensuelle" subtitle={`Recettes vs dépenses · ${year}`} rule />
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64"><Spinner /></div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyData} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis
                  tickFormatter={(v) => `${Math.round(v / 1000)}k€`}
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                />
                <Tooltip
                  cursor={{ fill: "#f8fafc" }}
                  contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 11, padding: "6px 12px" }}
                  formatter={(v) => `${Number(v).toLocaleString("fr-FR")} €`}
                />
                <Bar dataKey="Recettes" fill="#059669" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Dépenses" fill="#DC2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* ── Dons ── */}
      <section className="fade-up fade-up-3">
        <PageHeader
          title="Dons"
          subtitle={`${donations.length} don${donations.length !== 1 ? "s" : ""} enregistré${donations.length !== 1 ? "s" : ""}`}
          rule
          actions={
            !showDonationForm && (
              <button
                onClick={() => setShowDonationForm(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90 active:scale-[0.97] text-white"
                style={{ backgroundColor: "#059669" }}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
                Enregistrer un don
              </button>
            )
          }
        />

        {showDonationForm && (
          <form onSubmit={handleRecordDonation} className="mb-4 rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <PanelField label="Donateur" value={donationForm.donor_name} onChange={setDonationField("donor_name")} placeholder="Vide = don anonyme" />
              <PanelField label="Montant (€) *" type="number" value={donationForm.amount} onChange={setDonationField("amount")} required placeholder="100" />
              <PanelField label="Date de réception" type="date" value={donationForm.received_at} onChange={setDonationField("received_at")} />
              <PanelField label="Référence" value={donationForm.reference} onChange={setDonationField("reference")} placeholder="N° chèque, référence virement…" />
              <div className="col-span-2 space-y-2">
                <label className={labelCls}>Mode de réception *</label>
                <div className="grid grid-cols-3 gap-3">
                  {ADMIN_RECORDABLE_PROVIDERS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        donationForm.method === opt.value
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="donation-method"
                        value={opt.value}
                        checked={donationForm.method === opt.value}
                        onChange={setDonationField("method")}
                        className="accent-emerald-600"
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <PanelField label="Note" value={donationForm.note} onChange={setDonationField("note")} />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-1">
              <button
                type="button"
                onClick={() => { setShowDonationForm(false); setDonationForm(EMPTY_DONATION_FORM); }}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs hover:bg-slate-50 transition-all"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={recordingDonation}
                className="px-5 py-2 rounded-xl text-white text-xs font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
                style={{ backgroundColor: "#059669" }}
              >
                {recordingDonation ? "Enregistrement…" : "Enregistrer le don"}
              </button>
            </div>
          </form>
        )}

        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><Spinner /></div>
          ) : donations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm gap-2">
              <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 2v8m0 0v2m0-2c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>Aucun don enregistré.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <p className="text-[10px] text-slate-400 text-right px-4 pt-2 pb-0 sm:hidden">← Faites défiler →</p>
              <table className="text-sm" style={{ minWidth: "640px", width: "100%" }}>
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    {["Date", "Donateur", "Montant", "Mode", "Référence"].map((label) => (
                      <th key={label} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {donations.map((d, i) => (
                    <tr key={d.id} className={`hover:bg-slate-50/40 transition-colors ${i < donations.length - 1 ? "border-b border-slate-50" : ""}`}>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">
                        {new Date(d.received_at || d.created_at).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-4 py-3.5 font-semibold text-slate-900 whitespace-nowrap">{d.donor_name || "Don anonyme"}</td>
                      <td className="px-4 py-3.5 text-slate-700 font-medium whitespace-nowrap">{eur(d.amount)}</td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap capitalize">{PROVIDER_LABELS[d.method] || d.method}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-400 whitespace-nowrap">{d.reference || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default FinancePage;
